// ─── Post-sync next step (punch 0d89d480) ────────────────────────────────
// After a SUCCESSFUL sync, lead with what to fix first (P0 critical + P1 high)
// and one click into the PROJECT chat. That chat's fix checklist already
// pre-checks critical + high and composes the request, so this card does not
// prefill a message (that would duplicate the checklist). Failed syncs are
// handled by the sync failure banner, so this card renders nothing for them.
import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare, ShieldAlert, CheckCircle } from 'lucide-react';
import api from '../api';
import { openLiveSyncConversation } from '../lib/live-codex.js';

export interface NextStepCounts {
  unresolved: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface NextStepCopy {
  tone: 'urgent' | 'calm';
  title: string;
  detail: string;
  cta: string;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Copy for the card, or null when there is nothing to act on. */
export function nextStepCopy(c: NextStepCounts): NextStepCopy | null {
  const critical = Math.max(0, c.critical ?? 0);
  const high = Math.max(0, c.high ?? 0);
  const priority = critical + high;
  if (priority > 0) {
    const parts = [critical ? `${critical} critical` : null, high ? `${high} high` : null]
      .filter(Boolean)
      .join(' and ');
    return {
      tone: 'urgent',
      title: `${plural(priority, 'priority issue')} to fix first`,
      detail: `Your sync found ${parts}. They're pre-checked in chat \u2014 review the list and press Fix selected.`,
      cta: `Fix ${plural(priority, 'priority issue')}`,
    };
  }
  const unresolved = Math.max(0, c.unresolved ?? 0);
  if (unresolved > 0) {
    return {
      tone: 'calm',
      title: 'No urgent issues',
      detail: `Your sync found ${plural(unresolved, 'lower-priority finding')}. Review them in chat whenever you're ready.`,
      cta: 'Review in chat',
    };
  }
  return null;
}

export function SyncNextStepCard({ projectId, go, lastRunStatus, syncId }: {
  projectId: string;
  syncId?: string;
  go: (page: string) => void;
  /** Status of the most recent run; anything other than 'completed' hides the card. */
  lastRunStatus?: string;
}) {
  const [counts, setCounts] = useState<NextStepCounts | null>(null);
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState(false);
  const generation = useRef(0);
  useEffect(() => { generation.current += 1; setOpening(false); setOpenError(false); return () => { generation.current += 1; }; }, [projectId, syncId]);

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    api.findings.summary(projectId)
      .then((s) => {
        if (!alive) return;
        setCounts({
          unresolved: s.unresolved ?? 0,
          critical: s.critical ?? 0,
          high: s.high ?? 0,
          medium: s.medium ?? 0,
          low: s.low ?? 0,
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [projectId]);

  if (!projectId || (lastRunStatus && lastRunStatus !== 'completed') || !counts) return null;
  const legacyCopy = nextStepCopy(counts);
  if (!legacyCopy) return null;
  const copy = syncId ? { ...legacyCopy, detail: 'Codex will work through these findings, P0 first, and open a PR for your approval.', cta: 'Open Codex session' } : legacyCopy;
  const urgent = copy.tone === 'urgent';

  // Same path as the project page's "Open Chat": open (or create) the project chat.
  const openProjectChat = () => {
    if (opening) return;
    setOpening(true); setOpenError(false);
    const token = generation.current;
    if (syncId) {
      void openLiveSyncConversation(syncId).then((destination: string) => { if (generation.current === token) go(destination); })
        .catch(() => { if (generation.current === token) setOpenError(true); })
        .finally(() => { if (generation.current === token) setOpening(false); });
      return;
    }
    void api.createSubscriberChat(projectId)
      .then((r) => go(`subscriber-chat:chat:${r.chatSessionId}`))
      .catch(() => go(`subscriber-chat:project:${projectId}`))
      .finally(() => setOpening(false));
  };

  return (
    <div
      className="card"
      role="region"
      aria-label="Next step"
      style={{
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        flexWrap: 'wrap',
        borderColor: urgent ? 'var(--red)' : 'var(--border)',
        background: urgent ? 'var(--red-dim)' : undefined,
      }}
    >
      {urgent
        ? <ShieldAlert size={22} style={{ color: 'var(--red)', flexShrink: 0 }} />
        : <CheckCircle size={22} style={{ color: 'var(--green)', flexShrink: 0 }} />}
      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
        <div style={{ fontSize: 'var(--text-md)', fontWeight: 700, color: 'var(--text)' }}>{copy.title}</div>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: 2 }}>{copy.detail}</div>
        {openError && <p role="alert">Codex could not open yet. Your findings are preserved; try again.</p>}
      </div>
      <button
        type="button"
        className={`btn${urgent ? ' btn-primary' : ''}`}
        style={{ minHeight: 40, display: 'inline-flex', alignItems: 'center', gap: 6 }}
        onClick={openProjectChat}
        disabled={opening}
      >
        <MessageSquare size={14} /> {copy.cta}
      </button>
    </div>
  );
}

export default SyncNextStepCard;
